/**
 * Permanent, discreet minimap: track outline plus one colored dot per car.
 */

import type { Track, TrackSample } from '@microligue/shared';

export interface MinimapDot {
  x: number;
  y: number;
  color: string;
  isSelf: boolean;
}

const PAD = 8;

export class Minimap {
  private readonly ctx: CanvasRenderingContext2D;
  private readonly scale: number;
  private readonly offsetX: number;
  private readonly offsetY: number;
  /**
   * The static outline pre-rendered ONCE. Stroking the raw 500-4000 point
   * outline path every frame re-rasterized it 60×/s — measurable jank on
   * large circuits (audit multijoueur F7bis); blitting a bitmap is ~free.
   */
  private readonly outlineLayer: HTMLCanvasElement;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    track: Track,
  ) {
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D non disponible (mini-carte)');
    this.ctx = ctx;

    const w = canvas.width;
    const h = canvas.height;
    const b = track.bounds;
    this.scale = Math.min((w - PAD * 2) / (b.maxX - b.minX), (h - PAD * 2) / (b.maxY - b.minY));
    this.offsetX = PAD + (w - PAD * 2 - (b.maxX - b.minX) * this.scale) / 2 - b.minX * this.scale;
    this.offsetY = PAD + (h - PAD * 2 - (b.maxY - b.minY) * this.scale) / 2 - b.minY * this.scale;

    this.outlineLayer = document.createElement('canvas');
    this.outlineLayer.width = w;
    this.outlineLayer.height = h;
    const layer = this.outlineLayer.getContext('2d');
    if (layer) {
      const outline = new Path2D();
      const first = track.samples[0] as TrackSample;
      outline.moveTo(this.px(first.x), this.py(first.y));
      for (let i = 1; i < track.samples.length; i++) {
        const s = track.samples[i] as TrackSample;
        outline.lineTo(this.px(s.x), this.py(s.y));
      }
      outline.closePath();
      layer.strokeStyle = 'rgba(247, 243, 232, 0.85)';
      layer.lineWidth = 3;
      layer.lineJoin = 'round';
      layer.stroke(outline);
    }
  }

  private px(x: number): number {
    return x * this.scale + this.offsetX;
  }

  private py(y: number): number {
    return y * this.scale + this.offsetY;
  }

  render(dots: MinimapDot[]): void {
    const ctx = this.ctx;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    ctx.drawImage(this.outlineLayer, 0, 0);

    for (const dot of dots) {
      ctx.beginPath();
      ctx.arc(this.px(dot.x), this.py(dot.y), dot.isSelf ? 4.5 : 3.5, 0, Math.PI * 2);
      ctx.fillStyle = dot.color;
      ctx.fill();
      if (dot.isSelf) {
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 1.5;
        ctx.stroke();
      }
    }
  }
}
